"use client";

import { useRef, useEffect, memo } from "react";

interface LiveWaveformProps {
  stream: MediaStream | null;
  barWidth?: number;
  barGap?: number;
  barColor?: string;
  barMinHeight?: number;
  height?: number;
  className?: string;
}

function PureLiveWaveform({
  stream,
  barWidth = 3,
  barGap = 2,
  barColor = "currentColor",
  barMinHeight = 2,
  height = 24,
  className,
}: LiveWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (!stream || stream.getAudioTracks().length === 0) {
      // No stream — draw idle flat bars
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, rect.width, rect.height);

      const totalBar = barWidth + barGap;
      const barCount = Math.floor(rect.width / totalBar);
      ctx.fillStyle = barColor;
      const cy = rect.height / 2;
      for (let i = 0; i < barCount; i++) {
        const x = i * totalBar;
        ctx.globalAlpha = 0.25;
        ctx.beginPath();
        ctx.roundRect(x, cy - barMinHeight / 2, barWidth, barMinHeight, barWidth / 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      return;
    }

    // Set up Web Audio analyser
    const audioCtx = new AudioContext();
    audioCtxRef.current = audioCtx;
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.4;
    analyserRef.current = analyser;

    const source = audioCtx.createMediaStreamSource(stream);
    sourceRef.current = source;
    source.connect(analyser);

    const dataArray = new Uint8Array(analyser.frequencyBinCount);

    // Rolling history buffer — stores one amplitude value per bar slot
    const totalBar = barWidth + barGap;
    const initRect = canvas.getBoundingClientRect();
    const maxBars = Math.ceil(initRect.width / totalBar) + 2;
    const history: number[] = new Array(maxBars).fill(0);

    // Throttle how often we push a new sample (~20 pushes/sec for smooth scroll)
    let lastPush = 0;
    const pushInterval = 50; // ms between new bar samples

    const draw = (now: number) => {
      animFrameRef.current = requestAnimationFrame(draw);
      if (!canvasRef.current) return;
      const c = canvasRef.current;
      const cx2d = c.getContext("2d");
      if (!cx2d) return;

      const dpr = window.devicePixelRatio || 1;
      const rect = c.getBoundingClientRect();
      if (c.width !== rect.width * dpr || c.height !== rect.height * dpr) {
        c.width = rect.width * dpr;
        c.height = rect.height * dpr;
      }

      // Sample current amplitude
      analyser.getByteFrequencyData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
      const raw = sum / dataArray.length / 255;
      const avg = Math.min(1, raw * 2.5); // amplify so bars are taller

      // Push new sample at the throttled rate
      if (now - lastPush >= pushInterval) {
        lastPush = now;
        history.push(avg);
        // Keep buffer from growing unbounded
        const currentMax = Math.ceil(rect.width / totalBar) + 2;
        while (history.length > currentMax) history.shift();
      }

      cx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx2d.clearRect(0, 0, rect.width, rect.height);

      const barCount = Math.floor(rect.width / totalBar);
      const cy = rect.height / 2;
      cx2d.fillStyle = barColor;

      // Draw from history: rightmost bar = newest sample
      const startIdx = Math.max(0, history.length - barCount);
      for (let i = 0; i < barCount; i++) {
        const hIdx = startIdx + i;
        const val = hIdx < history.length ? history[hIdx] : 0;
        const barH = Math.max(barMinHeight, val * (rect.height - 2));
        const x = i * totalBar;
        const y = cy - barH / 2;

        cx2d.globalAlpha = 0.6 + val * 0.4;
        cx2d.beginPath();
        cx2d.roundRect(x, y, barWidth, barH, barWidth / 2);
        cx2d.fill();
      }
      cx2d.globalAlpha = 1;
    };

    animFrameRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      source.disconnect();
      analyser.disconnect();
      audioCtx.close().catch(() => {});
      analyserRef.current = null;
      audioCtxRef.current = null;
      sourceRef.current = null;
    };
  }, [stream, barWidth, barGap, barColor, barMinHeight, height]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: "100%", height, display: "block" }}
    />
  );
}

export const LiveWaveform = memo(PureLiveWaveform);
